<template>
  <div class="bg-white">
    <div class="main-width pb-12">
      <VBreadcrumb :options="options" class="my-5"></VBreadcrumb>
      <div class="filter-cruise">
        <cruise-list-filter :loading="isLoadingFilterData" :filter-data="filterData" v-bind.sync="searchData"
          @on-filter-change="onFilterChange" :keyword="keyword" @clearKeyword="clearKeyword"/>
        <nuxt-link :to="{
          path: '/cruise-map',
          query: {
            ...searchData,
            keyword,
            first_flight_time_start,
            first_flight_time_end,
          },
        }" target="_blank">
          <el-button class="absolute top-[200px] right-[20px] rounded" type="primary" >切换地图</el-button>
        </nuxt-link>
        <div class="result">
          <h3 class="result_heading_title">
            为您找到<span class="text-highlight">{{ pagination.total_count }} </span>个邮轮信息
          </h3>
          <div class="result_heading_sort">
            <sort-bar :sort-name="searchData.sort_name" :sort-type="searchData.sort_type" ref=""@on-sort-change="onSortChange"
              type="cruise_list" />
          </div>
        </div>
      </div>
      <div class="flex flex-wrap" v-if="cruiseItem?.length">
        <cruise-list-item v-for="cruise_item in cruiseItem" :key="cruise_item.id" :cruiseItem="cruise_item" class="cruise_item" />
      </div>
      <no-result v-else>
        <template #title>目前没有满足所有筛选项的产品，请尝试放大筛选范围</template>
      </no-result>
      <loading-more-btn :pagination="pagination" :isLoading="isLoading" @onLoadMore="onLoadMore" />
    </div>
  </div>
</template>

<script>
import { ref, reactive,  computed, useStore, useFetch, useRoute, useRouter, watch, nextTick } from "@nuxtjs/composition-api";
import CruiseListFilter from "~/views/company/CruiseListFilter.vue";
import CruiseListItem from "~/views/company/CruiseListItem.vue";
import SortBar from "~/views/product/ListProductSortBar.vue";
import NoResult from "@/views/product/components/ListNoResult";
import LoadingMoreBtn from "@/views/company/LoadingMoreBtn.vue";
import VBreadcrumb from "@/components/VBreadcrumb.vue";
import { getAisCruiseList } from "@/services/company";
import usePagination from "~/composables/usePagination";
import { pick } from "@/utils/helpers";

function createSearchData() {
  return {
    company_id: [],
    tonnage: [],
    class_id: [],
    first_flight_time: [],
    navistatus: [],
    keyword: "",
    sort_type: "",
    sort_name: "",
  };
}

export default {
  name: "FrontPcCruiseList",
  layout: "Main",
  head: { title: "邮轮列表" },
  components: { CruiseListFilter, CruiseListItem, SortBar, NoResult, LoadingMoreBtn, VBreadcrumb, },
  auth: false,
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const first_flight_time_end = ref("");
    const first_flight_time_start = ref("");
    const isLoadingFilterData = ref(true);
    const cruiseItem = ref([]);
    const newTonnage = ref([]);
    const searchData = reactive(createSearchData());
    const keyword = ref(route.value.query.q || "");

    const options = computed(() => {
      if (route.value.query.q) {
        return [
          { label: route.value.query.q + "邮轮搜索结果" },
          { label: "邮轮列表" },
        ];
      } else return [{ label: "邮轮列表" }];
    });

    const filterData = computed(() => {
      const { cruise_company: company_id, cruise_class, navistatus, tonnage, first_flight_time } = store.state.cruiseMap.filterData;
      return { company_id, cruise_class, navistatus, tonnage, first_flight_time };
    });

    const syncRouteQuery = async (query) => {
      Object.assign(searchData, pick(query, Object.keys(searchData)));
      await nextTick();
    };

    watch(
      () => route.value.query,
      async (query) => {
        await syncRouteQuery(query);
      },
      { immediate: true }
    );

    function onSearch() {
      const [[startValue, endValue] = ["", ""]] = searchData.first_flight_time.map((item) => item.split("-")).sort();
      first_flight_time_start.value = startValue ? `${startValue}-1-1` : "";
      first_flight_time_end.value = endValue ? `${endValue}-12-31` : first_flight_time_start.value;
      newTonnage.value = searchData.tonnage.map((items) => {
        const [min = "", max = ""] = items.split("-").sort((a, b) => a - b);
        return { min, max };
      });
      router.replace({
        query: { ...searchData },
      });
      execute().then((data) => {
        cruiseItem.value = data.list;
      });
    }

    const { execute, pagination, isLoading } = usePagination(
      ({ page_index }) =>
        getAisCruiseList({
          ...searchData,
          tonnage: newTonnage.value,
          first_flight_time_start: first_flight_time_start.value,
          first_flight_time_end: first_flight_time_end.value,
          keyword: keyword.value,
          page_index,
          page_size: 50,
        }),
      {},
      {
        immediate: false,
      }
    );

    useFetch(async () => {
      await store.dispatch("cruiseMap/fetchFilterData");
      let data = await execute(0, pagination);
      cruiseItem.value = cruiseItem.value.concat(data.list);
    });

    const onFilterChange = () => {
      onSearch();
    };

    const onSortChange = (sortParams = {}) => {
      Object.assign(searchData, sortParams);
      onSearch();
    };

    const onLoadMore = () => {
      pagination.page_index += 1;
      execute().then((data) => {
        cruiseItem.value = cruiseItem.value.concat(data.list);
      });
    };

    const clearKeyword = () => {
      router.replace({ query: {} });
      keyword.value = "";
      execute().then((data) => {
        cruiseItem.value = data.list;
      });
    };

    return {
      isLoadingFilterData,
      filterData,
      searchData,
      onFilterChange,
      onSortChange,
      cruiseItem,
      pagination,
      isLoading,
      onLoadMore,
      first_flight_time_start,
      first_flight_time_end,
      options,
      keyword,
      clearKeyword,
    };
  },
};
</script>

<style lang="scss" scoped>
.filter-cruise {
  border-radius: 1.5rem;
  margin-bottom: 30px;
  position: relative;
  width: 1200px;
  background: linear-gradient(180deg, #f3f5f8 0%, rgba(255, 255, 255, 0) 100%);
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.1);
}
.result {
  --tw-bg-opacity: 1;
  background-color: rgba(243, 245, 248, var(--tw-bg-opacity));
  border-bottom-right-radius: 1.5rem;
  border-bottom-left-radius: 1.5rem;
  display: -webkit-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  height: 60px;
  padding-left: 28px;
  padding-right: 10px;
  position: absolute;
  bottom: 0px;
  width: 100%;
  align-items: center;
}
.result_heading {
  display: -webkit-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  -webkit-align-items: center;
  align-items: center;
  padding-top: 2.5rem;
  padding-bottom: 2.5rem;
}
.result_heading_title {
  font-weight: 500;
  font-size: 1.25rem;
  line-height: 1.75rem;
  --tw-text-opacity: 1;
  color: rgba(25, 25, 25, var(--tw-text-opacity));
}
.result_heading_sort {
  font-size: 1rem;
  line-height: 1.5rem;
  margin-left: auto;
  --tw-text-opacity: 1;
  color: rgba(25, 25, 25, var(--tw-text-opacity));
}
.cruise_item:nth-child(5n + 5) {
  margin-right: 0px !important;
  color: red;
}
</style>
